import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import LuxPlusBenefitsModal from './modals/LuxPlusBenefitsModal'
import LuxPlusLabelContext from 'contexts/LuxPlusLabelContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusLogo from './Logos/LuxPlusLogo'
import FormatCurrency from '../../client/components/Common/FormatCurrency'

interface Props {
  offerPrice: number
  offerMemberPrice: number
  hasLuxPlusRate?: boolean
  hideUpsellModal?: boolean
  offer: App.BedbankOffer | App.BedbankOfferSummary
  savingAmount?: number
  /** The LuxPlus+ channel markdown in the member price: the extra saving a non-member gets by joining. */
  joinerExtraSaving?: number
}

function LuxLPPPricingLabel({
  offerPrice,
  offerMemberPrice,
  hasLuxPlusRate,
  offer,
  savingAmount,
  joinerExtraSaving,
}: Props) {
  const showModal = useContext(ModalContext)
  const providerPage = useContext(LuxPlusLabelContext)
  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  // Only a non-member can still join; a member never gets the markdown.
  const welcomeSaving = canViewLuxPlusBenefits ? undefined : joinerExtraSaving

  const toggleModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    const saveAmount = offerPrice - offerMemberPrice
    showModal(<LuxPlusBenefitsModal
      type={welcomeSaving ? 'member-price-offer-lpp' : 'member-price-offer-lpp-no-save-amount'}
      saveAmount={saveAmount}
      joinerExtraSaving={welcomeSaving}
      offer={offer}
      providerPage={providerPage}
    />)
  }, [offer, offerMemberPrice, offerPrice, providerPage, showModal, welcomeSaving])

  if (!luxPlusLPPEnabled || !hasLuxPlusRate) return null

  const memberDiscountsLabel = (
    <FormattedMessage
      id="H5eyYX68Ni40WBlLU0IV"
      defaultMessage="Member discounts with {luxPlusLogo}"
      description="Label indicating LuxPlus member discounts are available for a hotel offer"
      values={{ luxPlusLogo: <LuxPlusLogo colour="inherit" height={10} /> }}
    />
  )
  const savingAmountLabel = welcomeSaving ? (
    <FormattedMessage
      id="wPk0Ccidj7u35ZCGYJBF"
      defaultMessage="Save {formattedSaveAmount} when you join {logo}"
      description="Label showing the LuxPlus+ member saving, including the extra welcome saving, a non-member gets by joining"
      values={{
        formattedSaveAmount: <FormatCurrency format="roundedDollar" value={savingAmount!} />,
        logo: <LuxPlusLogo colour="inherit" height={10} />,
      }}
    />
  ) : (
    <FormattedMessage
      id="j2tq9hR_yRK2LStBlkjA"
      defaultMessage="Save {savingAmount} with {luxPlusLogo}"
      description="Label showing how much a user saves with LuxPlus membership on a hotel offer"
      values={{
        savingAmount: <FormatCurrency format="roundedDollar" value={savingAmount!} />,
        luxPlusLogo: <LuxPlusLogo colour="inherit" height={10} />,
      }}
    />
  )
  const savingContent = savingAmount ? savingAmountLabel : memberDiscountsLabel

  const label = (
    <Label
      kind="primary"
      variant="lux-plus"
      endIcon={!canViewLuxPlusBenefits ? <LineInfoCircleIcon /> : undefined}
    >
      {savingContent}
    </Label>
  )

  if (!canViewLuxPlusBenefits) return <Clickable onClick={toggleModal}>{label}</Clickable>

  return label
}

export default LuxLPPPricingLabel
